/**
 * v1 `Card` -> the channel shape the rail components (CategoryRail, ChannelCard, hero) render.
 * A media card keeps its slug, which is also the bridge channel slug the PDP and player load by.
 * Event and fighter cards open web's own pages; an article opens its bkfc.com page.
 */
export function cardToChannel(card) {
  const ref = card.slug || card.id;
  return {
    _id: card.id,
    slug: card.slug,
    title: card.title?.value || '',
    art: card.art,
    geo_ok: card.geo_ok,
    starts_at: card.starts_at || undefined,
    state: card.state,
    watch_on: card.watch_on || null,
    entityType: card.entity_type,
    link: { event: `/events/${ref}`, fighter: `/fighters/${ref}` }[card.entity_type] || undefined,
    href: card.entity_type === 'article' ? card.share_url : undefined,
  };
}

export const LAYOUT_TO_STYLE = { poster_2x3: 'poster' };

/** The See all card contract version (`lib`): 7 = slim cards, every rail variant. Bump on purpose. */
export const RAIL_LIB = 7;

/** See all (`GET /v1/rails/{id}`) answers a `variant`; develop's server still sends `layout`. */
export const VARIANT_TO_STYLE = { poster: 'poster', portrait: 'poster', poster_small: 'poster' };

const PAGE_FOR = {
  media: slug => `/watch/${slug}`,
  event: slug => `/events/${slug}`,
  fighter: slug => `/fighters/${slug}`,
};

/**
 * A See all `SlimCard` -> channel. Same as `cardToChannel`, except the server decides where a tap
 * goes (`link`): absent = the card's own page, `null` = not clickable, `navigate` = the target's
 * page, `play` = the player, `external` = a new tab. An event's date is its `event_day`.
 */
export function railCardToChannel(card) {
  const channel = cardToChannel(card);
  if (card.entity_type === 'event' && card.event_day) {
    channel.starts_at = card.event_day;
  }
  const { link } = card;
  if (link === undefined) {
    return channel;
  }
  channel.link = undefined;
  channel.href = undefined;
  const target = link?.target;
  if (link === null) {
    channel.clickable = false;
  } else if (link.action === 'external' && link.url) {
    channel.href = link.url;
  } else if (link.action === 'play' && target) {
    channel.link = `/watch/${target.slug || target.id}`;
  } else if (link.action === 'navigate' && target?.entity_type === 'series') {
    channel.link = `/series/${target.id}`;
  } else if (link.action === 'navigate' && PAGE_FOR[target?.entity_type]) {
    channel.link = PAGE_FOR[target.entity_type](target.slug || target.id);
  } else if (card.entity_type === 'article') {
    channel.href = card.share_url;
  } else {
    // An action or target this site does not know: the card's own page, as if `link` were absent.
    return cardToChannel(card);
  }
  return channel;
}

/**
 * `/v1/home` rails -> categories. Personal rails are placeholders whose rows come from
 * `/v1/me/state/*` with a session; continue watching and My List keep their existing source
 * for now, so they are skipped here.
 */
export function homeToCategories(home) {
  return (home?.rails || [])
    .filter(rail => !rail.personal && rail.items?.length)
    .map(rail => ({
      _id: rail.id,
      name: rail.title?.value || '',
      display_name: rail.title?.value || '',
      style: LAYOUT_TO_STYLE[rail.layout],
      layout: rail.layout,
      channels: rail.items.map(cardToChannel),
      // The home rail stops at max_items; `/rail/<id>` (GET /v1/rails/{id}) is the whole rail.
      max_items: rail.max_items,
      v1: true,
    }));
}
